import React, { useEffect, useState, useCallback } from 'react';

function WalletSidebar() {
    const [walletAddress, setWalletAddress] = useState('');
    const [balanceEth,   setBalanceEth]   = useState(null);
    const [balanceUsd,   setBalanceUsd]   = useState(null);
    const [loading,      setLoading]      = useState(false);
    const [error,        setError]        = useState('');

    // ── Fetch ETH balance directly via MetaMask JSON-RPC ──────────────────
    const fetchBalance = useCallback(async (address) => {
        if (!address || !window.ethereum) return;
        setLoading(true);
        setError('');
        try {
            // eth_getBalance returns hex wei for the currently connected network
            const hexBalance = await window.ethereum.request({
                method: 'eth_getBalance',
                params: [address, 'latest'],
            });
            // Parse hex wei string → ETH (parseInt handles large hex safely for display)
            const ethFloat = parseInt(hexBalance, 16) / 1e18;
            setBalanceEth(ethFloat.toFixed(6));

            // ── ETH price in USD from CoinGecko (public, no API key, CORS ok) ──
            try {
                const res  = await fetch(
                    'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd'
                );
                const data = await res.json();
                const usdPerEth = data?.ethereum?.usd;
                if (usdPerEth) {
                    setBalanceUsd((ethFloat * usdPerEth).toFixed(2));
                }
            } catch (_) {
                // CoinGecko unavailable — USD stays hidden
            }
        } catch (err) {
            console.error('Balance fetch failed:', err);
            setError('Could not fetch balance');
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Load currently connected MetaMask account ─────────────────────────
    const loadAccount = useCallback(async () => {
        if (!window.ethereum) return;
        try {
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });
            if (accounts.length > 0) {
                setWalletAddress(accounts[0]);
                sessionStorage.setItem('metamaskId', accounts[0]);
                fetchBalance(accounts[0]);
            }
        } catch (err) { console.error(err); }
    }, [fetchBalance]);

    useEffect(() => {
        if (!window.ethereum) return;

        // Only call eth_accounts after MetaMask's provider is connected;
        // calling it before the internal RPC connection is ready throws
        // "Failed to connect to MetaMask".
        if (window.ethereum.isConnected()) {
            loadAccount();
        } else {
            window.ethereum.once('connect', loadAccount);
        }

        // Re-fetch when user switches account
        const onAccountChange = (accounts) => {
            const addr = accounts[0] || '';
            setWalletAddress(addr);
            setBalanceEth(null);
            setBalanceUsd(null);
            if (addr) fetchBalance(addr);
        };

        // Re-fetch when user switches network (chain)
        const onChainChange = () => {
            setBalanceEth(null);
            setBalanceUsd(null);
            loadAccount();
        };

        window.ethereum.on('accountsChanged', onAccountChange);
        window.ethereum.on('chainChanged',    onChainChange);

        return () => {
            window.ethereum.removeListener('connect',         loadAccount);
            window.ethereum.removeListener('accountsChanged', onAccountChange);
            window.ethereum.removeListener('chainChanged',    onChainChange);
        };
    }, [loadAccount, fetchBalance]);

    // ── Connect wallet ─────────────────────────────────────────────────────
    const connectWallet = async () => {
        const isMobile  = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const isIOS     = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isAndroid = /Android/i.test(navigator.userAgent);

        if (window.ethereum) {
            if (isMobile) {
                window.location.href = 'https://metamask.app.link/dapp/' + window.location.href;
                setTimeout(() => {
                    if (isIOS)          window.location.href = 'https://apps.apple.com/app/metamask/id1438144202';
                    else if (isAndroid) window.location.href = 'https://play.google.com/store/apps/details?id=io.metamask';
                }, 1500);
            } else {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                    setWalletAddress(accounts[0]);
                    fetchBalance(accounts[0]);
                } catch (err) { console.error(err); }
            }
        } else {
            alert('MetaMask is not installed. Please install it to proceed.');
        }
    };

    const shortAddr = walletAddress
        ? `${walletAddress.substring(0, 6)}...${walletAddress.substring(38)}`
        : null;

    return (
        <div>
            {/* ── Header ── */}
            <div className="wallet-funds">
                <div className="wallet">My Wallet</div>
                <div className="provider-button">
                    {walletAddress ? (
                        <span title={walletAddress}>Connected: {shortAddr}</span>
                    ) : (
                        <button
                            onClick={connectWallet}
                            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0 }}
                        >
                            Connect MetaMask
                        </button>
                    )}
                </div>
            </div>

            {/* ── ETH Balance ── */}
            <div className="wallet-Amount">
                <p className="p-1">ETH Balance</p>
                <p className="p-2">
                    {loading
                        ? 'Loading…'
                        : error
                            ? error
                            : balanceEth !== null
                                ? `${balanceEth} ETH`
                                : walletAddress ? '—' : 'Not connected'}
                </p>
            </div>

            {/* ── USD Value ── */}
            <div className="wallet-Amount" style={{ marginTop: '-10px' }}>
                <p className="p-1">USD Value</p>
                <p className="p-2">
                    {balanceUsd !== null
                        ? `$${balanceUsd} USD`
                        : balanceEth !== null && !loading ? '—' : ''}
                </p>
            </div>

            {/* ── Add Funds → MegaETH testnet faucet ── */}
            <a
                href="https://testnet.megaeth.com/#2"
                target="_blank"
                rel="noopener noreferrer"
                className="wallet-button"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
                Add Funds
            </a>
        </div>
    );
}

export default WalletSidebar;
